import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Lang } from '../types';
interface NavbarProps {
  lang: Lang;
  setLang: (lang: Lang) => void;
}
const navLinks = [{
  href: '#gallery',
  en: 'Gallery',
  es: 'Galería'
}, {
  href: '#benefits',
  en: 'Benefits',
  es: 'Beneficios'
}, {
  href: '#how-it-works',
  en: 'How It Works',
  es: 'Cómo Funciona'
}, {
  href: '#booking',
  en: 'Book Session',
  es: 'Reservar Sesión'
}, {
  href: '#faq',
  en: 'FAQ',
  es: 'Preguntas Frecuentes'
}, {
  href: '#testimonials',
  en: 'Testimonials',
  es: 'Testimonios'
}];
export function Navbar({
  lang,
  setLang
}: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleNavClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setIsMenuOpen(false);
    // Small delay to let menu close animation start, then scroll
    setTimeout(() => {
      const id = href.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        const navbarHeight = 80;
        const top = el.getBoundingClientRect().top + window.scrollY - navbarHeight;
        window.scrollTo({
          top,
          behavior: 'smooth'
        });
      }
    }, 100);
  }, []);
  return <motion.nav initial={{
    y: -100
  }} animate={{
    y: 0
  }} transition={{
    duration: 0.6,
    ease: [0.16, 1, 0.3, 1]
  }} className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 border-b ${scrolled || isMenuOpen ? 'bg-studio-bg/95 backdrop-blur-md border-studio-border' : 'bg-transparent border-transparent'}`} data-id="element-253">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between relative z-50" data-id="element-254">
        <div className="flex flex-col" data-id="element-255">
          <a href="#" className="font-playfair text-xl md:text-2xl font-semibold tracking-wide text-studio-text" data-id="element-256">
            BARBOXA <span className="text-studio-accent italic" data-id="element-257">INK</span>
          </a>
          <a href="tel:+17867620617" className="flex items-center gap-1.5 text-[11px] sm:text-xs text-studio-accent font-medium tracking-wider uppercase hover:text-studio-text transition-colors" data-id="element-258">
            📞 <span data-id="element-259">{lang === 'en' ? 'Call Now' : 'Llamar'}</span>
            <span className="text-studio-muted" data-id="element-260">786-762-0617</span>
          </a>
        </div>

        <div className="flex items-center gap-4 sm:gap-6" data-id="element-261">
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="lg:hidden text-studio-text hover:text-studio-accent transition-colors p-1" aria-label="Toggle menu" data-id="element-262">
            {isMenuOpen ? <X size={24} data-id="element-263" /> : <Menu size={24} data-id="element-264" />}
          </button>

          <div className="flex items-center bg-studio-bgAlt border border-studio-border rounded-full p-1" data-id="element-265">
            <button onClick={() => setLang('en')} className={`px-3 py-1 text-sm rounded-full transition-colors ${lang === 'en' ? 'bg-studio-accent text-studio-bg font-medium' : 'text-studio-muted hover:text-studio-text'}`} data-id="element-266">
              EN
            </button>
            <button onClick={() => setLang('es')} className={`px-3 py-1 text-sm rounded-full transition-colors ${lang === 'es' ? 'bg-studio-accent text-studio-bg font-medium' : 'text-studio-muted hover:text-studio-text'}`} data-id="element-267">
              ES
            </button>
          </div>

          <a href="#booking" onClick={e => handleNavClick(e, '#booking')} className="hidden md:inline-flex items-center justify-center px-6 py-2 border border-studio-accent text-studio-accent hover:bg-studio-accent hover:text-studio-bg transition-colors duration-300 text-sm font-medium tracking-wide uppercase" data-id="element-268">
            {lang === 'en' ? 'Book Now' : 'Reservar'}
          </a>
        </div>
      </div>

      {/* Mobile Dropdown Menu */}
      <AnimatePresence data-id="element-269">
        {isMenuOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="lg:hidden absolute top-full left-0 right-0 bg-studio-bg/95 backdrop-blur-md border-b border-studio-border overflow-hidden shadow-xl" data-id="element-270">
            <div className="px-6 py-5 flex flex-col gap-4" data-id="element-271">
              {navLinks.map(link => <a key={link.href} href={link.href} onClick={e => handleNavClick(e, link.href)} className="text-studio-text font-medium text-lg hover:text-studio-accent transition-colors flex items-center justify-between py-1" data-id="element-272">
                  {lang === 'en' ? link.en : link.es}
                  <span className="text-studio-accent opacity-50 text-sm" data-id="element-273">
                    →
                  </span>
                </a>)}

              {/* Divider */}
              <div className="border-t border-studio-border my-1" data-id="element-274" />

              {/* Get Directions */}
              <a href="https://www.google.com/maps/dir/?api=1&destination=2116+SW+8th+St,+Miami+FL+33135" target="_blank" rel="noopener noreferrer" className="text-studio-text font-medium text-lg hover:text-studio-accent transition-colors flex items-center justify-between py-1" data-id="element-275">
                <span className="flex items-center gap-2" data-id="element-276">
                  📍 {lang === 'en' ? 'Get Directions' : 'Cómo Llegar'}
                </span>
                <span className="text-studio-muted text-xs" data-id="element-277">
                  2116 SW 8th St, Miami
                </span>
              </a>

              {/* Book Now CTA */}
              <a href="#booking" onClick={e => handleNavClick(e, '#booking')} className="mt-1 inline-flex items-center justify-center px-6 py-3 bg-studio-accent text-studio-bg font-semibold tracking-wide uppercase transition-transform active:scale-95" data-id="element-278">
                📅 {lang === 'en' ? 'Book Now' : 'Reservar'}
              </a>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.nav>;
}